// NotificationBell — header bell icon with unread badge + dropdown panel.
// Shows the 8 most recent notifications. "View all" links to /notifications.
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const CATEGORY_ICONS = {
  booking:    'ph-ticket',
  payment:    'ph-credit-card',
  tournament: 'ph-trophy',
  live_match: 'ph-cricket',
  auction:    'ph-gavel',
  operations: 'ph-megaphone',
  shop:       'ph-shopping-bag',
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const { items, unread, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleClick = (n) => {
    if (!n.read_at) markRead(n.id);
    if (n.deep_link) navigate(n.deep_link);
    setOpen(false);
  };

  const recent = items.slice(0, 8);

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition"
        aria-label="Notifications"
      >
        <i className="ph-bold ph-bell text-xl text-[#2B3A67]" />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E54562] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-12 w-[360px] max-w-[92vw] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-bold text-[#2B3A67]">Notifications</h3>
            {unread > 0 && (
              <button
                data-testid="notification-mark-all-read"
                onClick={markAllRead}
                className="text-xs font-semibold text-[#E54562] hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {recent.length === 0 ? (
              <div data-testid="notification-empty-state" className="text-center py-12 px-6">
                <i className="ph-light ph-bell-slash text-4xl text-gray-300 mb-3 block" />
                <p className="text-sm text-gray-500">You're all caught up</p>
              </div>
            ) : (
              recent.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition flex gap-3 ${n.read_at ? 'opacity-70' : 'bg-blue-50/30'}`}
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#2B3A67]/10 flex items-center justify-center">
                    <i className={`ph-bold ${CATEGORY_ICONS[n.category] || 'ph-bell'} text-[#2B3A67]`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-semibold text-sm text-[#2B3A67] truncate">{n.title}</p>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">{timeAgo(n.created_at)}</span>
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2">{n.body}</p>
                  </div>
                  {!n.read_at && (
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-[#E54562] mt-2" />
                  )}
                </button>
              ))
            )}
          </div>

          <button
            data-testid="notification-view-all"
            onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="w-full py-3 text-center text-sm font-semibold text-[#E54562] border-t border-gray-100 hover:bg-gray-50"
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
}
